/*! JointJS+ v4.1.0 - HTML5 Diagramming Framework

Copyright (c) 2024 client IO

 2024-12-04


This Source Code Form is subject to the terms of the JointJS+ License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/


import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';

import { StencilService } from './services/stencil-service';
import { ToolbarService } from './services/toolbar-service';
import { InspectorService } from './services/inspector-service';
import { HaloService } from './services/halo-service';
import { KeyboardService } from './services/keyboard-service';
import { NavigatorService } from './services/navigator-service';
import RappidService from './services/kitchensink-service';
import { ManagedataService } from 'src/app/providers/input/managedata/managedata.service';
import { AppService } from 'src/app/providers/appservice/app.service';
import { CanvasService } from 'src/app/providers/canvas/canvas.service';
import { itDiagnosticsService } from 'src/app/providers/input/itdiagnostics.service';

import { getAppRectangle, dynamicGroups, setAppRectangleChanges } from './config/stencil';
import { PortfolioThemePopupComponent } from '../../portfolio-theme-popup/portfolio-theme-popup.component';
import { DialogService } from 'primeng/dynamicdialog';
import { SupplierheatmpComponent } from 'src/app/supplierheatmp/supplierheatmp.component';
import { ApplicationdataPopupComponent } from '../../applicationdata-popup/applicationdata-popup.component';
import { OtheatmpComponent } from 'src/app/otheatmp/otheatmp.component';
import { LoadingService } from 'src/app/providers/loading/loading.service';
import { SharedService } from 'src/app/modules/home/data.service';
import { debounce } from 'lodash';
import { faTaToggle, observeAttributes, updateTextFromAttr } from './utils/dom';
import { dia, g } from '@joint/plus';
import * as joint from '@joint/plus';
import { DashboardService } from 'src/app/providers/home/dashboard.service';
import { ConfirmationService } from 'primeng/api';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { editorModules } from './config/editor';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';

import Quill from 'quill';
import { combineLatest, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
const FontAttributor = Quill.import('attributors/class/font');
FontAttributor.whitelist = ['arial', 'verdana', 'calibri', 'roboto', 'times-new-roman', 'tahoma'];
Quill.register(FontAttributor, true);

@Component({
  standalone: false,
    selector: 'app-jointjs-canvas',
    templateUrl: './jointjscanvas.component.html',
    styleUrls: ['./jointjscanvas.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class JointJsCanvasComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    @Input()
    canvasType: any;
    @Input()
    view: string;
    @Input()
    canvasData: string;
    // Containers
    @ViewChild('jointApp', { static: true })
    jointApp: ElementRef;

    @ViewChild('paperContainer', { static: true })
    paperContainer: ElementRef;

    @ViewChild('stencilContainer', { static: true })
    stencilContainer: ElementRef;

    @ViewChild('toolbarContainer', { static: true })
    toolbarContainer: ElementRef;

    @ViewChild('inspectorContainer', { static: true })
    inspectorContainer: ElementRef;

    @ViewChild('navigatorContainer', { static: true })
    navigatorContainer: ElementRef;

    // Additional inspector elements
    @ViewChild('openGroupsButton', { static: true })
    openGroupsButton: ElementRef;

    @ViewChild('closeGroupsButton', { static: true })
    closeGroupsButton: ElementRef;

    @ViewChild('inspectorHeader', { static: true })
    inspectorHeader: ElementRef;

    @ViewChild('inspectorContent', { static: true })
    inspectorContent: ElementRef;
    private rappid: RappidService;
    private stencilData: any = {};
    private otHeaders: any[] = [];
    public mode: string = 'view';
    public canvasTabs: any = [];
    public activeTab: any = 0;
    public tabRenameDialog: boolean = false;
    public tabModel: any = {};
    private saveCanvasFunc: any = debounce(this.saveCanvas, 1000)
    public fataList = [
        { label: 'FA', value: 'faQusAvg' },
        { label: 'TA', value: 'taQusAvg' }
    ];
    public faTaColorKey: string = 'taQusAvg';
    public isApplicationsExist: boolean = false;

    public customPropertiesList = [
        { label: 'AI Transformation Opportunity', value: 'AI Transformation Opportunity' },
        { label: 'Digital Transformation Opportunity', value: 'Digital Transformation Opportunity' },
        { label: 'Efficiency Opportunity', value: 'Efficiency Opportunity' },
        { label: 'Innovation Opportunity', value: 'Innovation Opportunity' },
        { label: 'Other Opportunity', value: 'Other Opportunity' },
        { label: 'Clear Selection', value: '' }
    ];
    public selectedCustomProperty: string = '';
    public isCustomPropertiesExist: boolean = false;
    private fileCategory: string = 'canvas';
    private paperStyle: any = {}
    public textEditor: any = {}
    public elementOpportunityEditor: any = {}
    public elementOpportunityDescriptionPopup: any = {}
    public editorModules: any = editorModules
    private presentationObserver: MutationObserver
    // public canvasData: string;

    // Constants for highlighting
    private readonly HIGHLIGHT_STROKE_COLOR = '#000000';
    private readonly HIGHLIGHT_STROKE_WIDTH = 4;
    private readonly DEFAULT_STROKE_COLOR = '#000000';
    private readonly DEFAULT_STROKE_WIDTH = 1;
    private readonly DEFAULT_OPACITY = 1;
    private readonly BODY_STROKE_PATH = 'body/stroke';
    private readonly BODY_STROKE_WIDTH_PATH = 'body/strokeWidth';
    private readonly BODY_STROKE_OPACITY_PATH = 'body/strokeOpacity';
    private readonly LINE_STROKE_PATH = 'line/stroke';
    private readonly LINE_STROKE_WIDTH_PATH = 'line/strokeWidth';
    private readonly LINE_STROKE_OPACITY_PATH = 'line/strokeOpacity';
    private readonly IMAGE_STROKE_PATH = 'image/stroke';
    private readonly IMAGE_STROKE_WIDTH_PATH = 'image/strokeWidth';
    private readonly STANDARD_IMAGE_TYPE = 'standard.Image';

    constructor(private element: ElementRef, public manageDataService: ManagedataService, private appService: AppService, private dialogService: DialogService, private canvasService: CanvasService, public loadingService: LoadingService, private sharedService: SharedService, private dashboardService: DashboardService, private confirmationService: ConfirmationService, private itDiagnosticsService: itDiagnosticsService, private route: ActivatedRoute, private router: Router, private cdr: ChangeDetectorRef) {

    }


    ngOnInit() {
        const services = {
            stencilService: new StencilService(this.stencilContainer.nativeElement),
            toolbarService: new ToolbarService(this.toolbarContainer.nativeElement, this.resetCanvasSize.bind(this)),
            inspectorService: new InspectorService({
                openGroupsButton: this.openGroupsButton.nativeElement,
                closeGroupsButton: this.closeGroupsButton.nativeElement,
                container: this.inspectorContainer.nativeElement,
                header: this.inspectorHeader.nativeElement,
                content: this.inspectorContent.nativeElement,
                jointApp: this.jointApp.nativeElement,
                uploadImage: this.uploadImage.bind(this),
                tabsList: this.tabsList.bind(this),
                showEditor: this.showEditor.bind(this),
                showElementOpportunityEditor: this.showElementOpportunityEditor.bind(this),
                view: this.view,
                getSelectedCustomProperty: () => this.selectedCustomProperty
            }),
            haloService: new HaloService(),
            keyboardService: new KeyboardService(),
            navigatorService: new NavigatorService(this.navigatorContainer.nativeElement, this.resetCanvasSize.bind(this)),
        }

        this.rappid = new RappidService(
            this.element.nativeElement,
            this.paperContainer.nativeElement,
            services,
            this.onElementClick.bind(this),
            this.handleCanvasChange.bind(this)
        );

        this.rappid.startRappid();
        this.setViewMode();
        this.getTabs();
        this.rappid.stencilService.stencil.on('group:open', (id: string) => {
            if (id === 'applications') {
                faTaToggle(id, (colorKey: string) => {
                    this.faTaColor(id, colorKey)
                });
            }
        })
        this.checkPresentationView()
        // this.rappid.graph.fromJSON(JSON.parse(sampleGraphs.emergencyProcedure), { ignoreUndoRedo: true });
        this.sharedService.refreshScreen$.subscribe(response => {
            if (response === 'itorgchart' || response === 'businessoptModel' || response === 'businesscapFramework' || response === 'tal' || response === 'taritarch' || response === 'itblueprint' || response === 'orgchart') {
                this.getTabs();
                this.cdr.markForCheck();
            }
        });
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.getTabs();
                this.cdr.markForCheck();
            });
    }

    handleCanvasChange() {
        if (this.mode === 'edit') {
            this.saveCanvasFunc()
        }
    }

    async setViewMode() {
        this.mode = 'view';
        this.loadingService.apiLoader = true;
        await this.getStencilData();
        this.renderGraph();
    }

    async setEditMode() {
        this.mode = 'edit';
        this.loadingService.apiLoader = true;
        await this.getStencilData();
        this.renderGraph();
    }

    parseData(str: string) {
        this.isApplicationsExist = false;
        this.isCustomPropertiesExist = false;
        try {
            const data = JSON.parse(str);
            if (data.cells) {
                data.cells.forEach((cell: any, index: number) => {
                    if (cell.attrs?.meta?.type) {
                        const item = this.stencilData[cell.attrs.meta.type].find((item: any) => item._id === cell.attrs.meta.id);
                        const group = dynamicGroups.find((group: any) => group.id === cell.attrs.meta.type);
                        setAppRectangleChanges(cell, item, group)
                        if (group.id === 'applications') {
                            this.isApplicationsExist = true;
                        }
                    }
                    // Check if element has custom property
                    if (cell.data?.customProperty) {
                        this.isCustomPropertiesExist = true;
                    }
                    if (cell.attrs?.meta?.url) {
                        this.dashboardService.getSelectedImage(cell.attrs.meta.url, this.fileCategory).subscribe({
                            next: (signedUrl) => {
                                cell.attrs.image["xlink:href"] = signedUrl;
                                this.rappid.graph.getCell(cell.id)?.attr('image/xlink:href', signedUrl);
                                this.cdr.markForCheck();
                            },
                            error: () => this.cdr.markForCheck()
                        });
                    }
                });
            }
            return data;
        } catch (e) {
            return false;
        }
    }

    async getStencilData() {
        let promises: any = [];
        dynamicGroups.forEach((group: any) => {
            // Special handling for OT data - use getOTDataWithHeaders
            if (group.id === 'otdata') {
                promises.push(
                    new Promise((resolve, reject) =>
                        this.itDiagnosticsService.getOTDataWithHeaders(this.appService.strategyId).subscribe({
                            next: (response: any) => {
                                this.stencilData[group.id] = response.otData || [];
                                this.otHeaders = response.headers || [];
                                if (this.mode === 'edit') {
                                    this.renderStencilGroup(response.otData || [], group);
                                }
                                resolve(true);
                            },
                            error: (err) => {
                                console.error('Error loading OT data:', err);
                                this.cdr.markForCheck();
                                resolve(false);
                            }
                        })
                    )
                );
            } else {
                promises.push(
                    new Promise((resolve) => (this.manageDataService as any)[group.func](this.appService.strategyId).subscribe({
                        next: (res: any) => {
                            this.stencilData[group.id] = res;
                            if (this.mode === 'edit') {
                                this.renderStencilGroup(res, group);
                            }
                            this.cdr.markForCheck();
                            resolve(true);
                        },
                        error: () => {
                            this.cdr.markForCheck();
                            resolve(false);
                        }
                    }))
                );
            }
        });
        await Promise.all(promises)
    }

    renderStencilGroup(data: any, group: any) {
        const shapes = data.map((item: any) => {
            return getAppRectangle(item, group);
        });
        this.rappid.stencilService.stencil.loadGroup(shapes, group.id);
    }

    projectsPopup(projectData: any) {
        this.dialogService.open(PortfolioThemePopupComponent, {
            header: 'PROJECT INFORMATION',
            width: '1405px',
            data: { projectData, businessObjectives: {}, screen: 'portfolio-prioritization' }
        });
    }

    applicationsPopup(applicationData: any) {
        this.dialogService.open(ApplicationdataPopupComponent, {
            header: 'APPLICATION INFO',
            width: '1405px',
            data: { appData: applicationData, businessObjectives: {}, selectedObjective: {}, screen: 'businessobjectives' }
        });
    }

    suppliersPopup(supplierData: any) {
        this.dialogService.open(SupplierheatmpComponent, {
            header: 'SUPPLIER INFO',
            width: '1405px',
            closable: true,
            data: { supdata: supplierData, selectedObjective: {}, screen: 'businessobjectives' }
        });
    }

    otdataPopup(otData: any) {
        this.dialogService.open(OtheatmpComponent, {
            header: 'OT ASSET INFORMATION',
            width: '1405px',
            data: { otdata: otData, headers: this.otHeaders, selectedObjective: {}, screen: 'businessobjectives' }
        });
    }

    getTabs() {
        this.canvasService.getTabs(this.canvasType, this.appService.strategyId).subscribe({
            next: (res) => {
                if (res.length) {
                    this.canvasTabs = res;
                    this.getCanvas();
                } else {
                    if (this.view !== 'presentation') {
                        this.tabRenameDialog = true;
                        this.setEditMode();
                    }
                }
                this.cdr.markForCheck();
            },
            error: () => {
                this.cdr.markForCheck();
            },
            complete: () => {
                // console.info('complete')
            }
        })
    }

    tabsList() {
        return this.canvasTabs.map((tab: any) => {
            return {
                value: tab._id,
                content: tab.tab_name,
            };
        })
    }

    getCanvas() {
        this.canvasService.getCanvasData(this.canvasType, this.appService.strategyId, this.canvasTabs[this.activeTab]._id).subscribe({
            next: (res) => {
                this.canvasData = res.canvas;
                this.canvasTabs[this.activeTab] = res;
                this.renderGraph();
                this.cdr.markForCheck();
            },
            error: () => {
                this.cdr.markForCheck();
            },
            complete: () => {
                // console.info('complete')
            }
        })
    }

    renderGraph() {
        this.rappid.setMode(this.mode);
        this.rappid.graph.fromJSON(this.parseData(this.canvasData) || { cells: [] }, { ignoreUndoRedo: true });
        setTimeout(() => {
            if (this.mode === 'edit') {
                this.rappid.paper.setInteractivity(true);
            } else {
                this.rappid.paper.setInteractivity(false);
            }
            this.rappid.graph.getElements().forEach(el => {
                const view = this.rappid.paper.findViewByModel(el);
                if (view) updateTextFromAttr(view);
            });

            // Reapply custom property filter if active
            if (this.selectedCustomProperty && this.view === 'presentation') {
                this.filterByCustomProperty(this.selectedCustomProperty);
            }
        }, 50);
        this.loadingService.apiLoader = false;
        this.cdr.markForCheck();
    }

    saveCanvas() {
        const graphData = this.rappid.graph.toJSON();
        
        // Remove data field if customProperty is empty/null/undefined
        if (graphData.cells && Array.isArray(graphData.cells)) {
            graphData.cells.forEach((cell: any) => {
                if (cell.data && cell.data.customProperty !== undefined) {
                    const customProperty = cell.data.customProperty;
                    // Check if customProperty is empty, null, undefined, or empty string
                    if (customProperty === null || customProperty === undefined || customProperty === '' || String(customProperty).trim() === '') {
                        delete cell.data;
                    }
                }
            });
        }
        
        const data = JSON.stringify(graphData);
        const tab = this.canvasTabs[this.activeTab]
        this.canvasService.saveCanvasData({ ...tab, type: this.canvasType, canvas: data }, this.appService.strategyId).subscribe({
            next: (v) => {
                this.loadingService.apiLoader = false;
            },
            error: (e) => {
                this.cdr.markForCheck();
                this.sharedService.alertNotification('error', 'Unable to Save', e);
            },
            complete: () => {
                // this.loaderService.hide();
                // if (shouldShowToast) {
                //   this.sharedService.alertNotification('success', 'Saved Successfully', '');
                // }
            }
        })
    }

    onElementClick(model: { id?: string; attrs?: any }) {
        if (this.view === 'presentation' && this.mode === 'view' && model.id != null) {
            const cell = this.rappid.graph.getCell(model.id);
            if (cell) {
                const cellData = cell.get('data') || {};
                const customProperty = cellData.customProperty;

                // Check if element is highlighted (has matching opportunity property)
                const isHighlighted = customProperty &&
                    customProperty !== '' &&
                    String(customProperty).includes('Opportunity') &&
                    this.selectedCustomProperty === customProperty;

                if (isHighlighted) {
                    // When highlighted, navigate to tab is disabled
                    // Only show opportunity description popup if description exists
                    const description = cellData.elementOpportunityDescription || '';
                    if (description && description.trim() !== '') {
                        this.showElementOpportunityDescriptionPopup(customProperty, description);
                    }
                    // If no description, do nothing (don't navigate to tab)
                    return;
                }
            }
        }

        // Original behavior for non-presentation mode or non-highlighted elements
        // Navigate to tab works only when element is NOT highlighted
        if (model.attrs?.meta?.type && this.mode === 'view') {
            this.stencilData[model.attrs.meta.type].forEach((item: any) => {
                if (item._id === model.attrs.meta.id) {
                    if (typeof (this as any)[`${model.attrs.meta.type}Popup`] === 'function') {
                        (this as any)[`${model.attrs.meta.type}Popup`](item);
                    }
                }
            })
        } else if (model.attrs?.meta?.tab && this.mode === 'view') {
            this.canvasTabs.forEach((tab: any, index: number) => {
                if (tab._id === model.attrs.meta.tab) {
                    this.setActiveTab(index);
                }
            })
        }
    }

    addNewTab() {
        this.canvasTabs[this.activeTab].data = JSON.stringify(this.rappid.graph.toJSON());
        this.tabModel = {};
        this.tabRenameDialog = true;
    }


    setActiveTab(index: number) {
        this.canvasTabs[this.activeTab].canvas = JSON.stringify(this.rappid.graph.toJSON());
        if (this.mode === 'edit') {
            this.saveCanvas();
        }
        this.activeTab = index;
        if (this.mode === 'view' || !this.canvasTabs[this.activeTab].canvas) {
            this.getCanvas();
        } else {
            this.canvasData = this.canvasTabs[this.activeTab].canvas;
            this.renderGraph();
        }
    }

    getSortOrder() {
        let sortOrder = 0;
        this.canvasTabs.forEach((tab: any) => {
            if (tab.sort_order > sortOrder) {
                sortOrder = tab.sort_order;
            }
        })
        return sortOrder + 1;
    }

    saveTab() {
        this.loadingService.apiLoader = true;
        if (this.canvasTabs.length) {
            this.saveCanvas()
        }
        if (this.tabModel._id) {
            this.tabModel.canvas = JSON.stringify(this.rappid.graph.toJSON())
        } else {
            this.tabModel.canvas = JSON.stringify({ cells: [] })
            this.tabModel.sort_order = this.getSortOrder()
            this.tabModel.tab_name = this.tabModel.tab_name || `Tab ${this.canvasTabs.length + 1}`
        }
        this.canvasService.saveCanvasData({ ...this.tabModel, type: this.canvasType }, this.appService.strategyId).subscribe({
            next: (v) => {
                if (!this.tabModel._id) {
                    this.activeTab = this.canvasTabs.length;
                }
                if (this.canvasTabs.length === 0) {
                    this.canvasTabs = [this.tabModel];
                }
                this.tabRenameDialog = false;
                this.getTabs()
            },
            error: (e) => {
                this.cdr.markForCheck();
                this.sharedService.alertNotification('error', 'Unable to Save', e);
            },
            complete: () => {
                // this.loaderService.hide();
                // if (shouldShowToast) {
                //   this.sharedService.alertNotification('success', 'Saved Successfully', '');
                // }
            }
        })

    }
    editTab(tab: any) {
        this.tabRenameDialog = true;
        this.tabModel = tab;
    }

    reorderTabs(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.canvasTabs, event.previousIndex, event.currentIndex);
        this.canvasTabs.forEach((tab: any, index: number) => {
            tab.sort_order = index + 1;
        })
        const tabs = this.canvasTabs.map((tab: any) => {
            return {
                _id: tab._id,
                sort_order: tab.sort_order
            }
        })
        this.canvasService.reorderTabs(tabs, this.appService.strategyId).subscribe({
            next: () => {
                this.loadingService.apiLoader = false;
            },
            error: () => {
                this.loadingService.apiLoader = false;
                this.cdr.markForCheck();
                this.sharedService.alertNotification('error', 'Unable to Save', 'Unable to save the order of tabs');
            },
            complete: () => {
                this.loadingService.apiLoader = false;
                this.cdr.markForCheck();
            }
        })
        if (this.activeTab === event.previousIndex) {
            this.activeTab = event.currentIndex;
        } else if (
            this.activeTab > event.previousIndex &&
            this.activeTab <= event.currentIndex
        ) {
            this.activeTab--;
        } else if (
            this.activeTab < event.previousIndex &&
            this.activeTab >= event.currentIndex
        ) {
            this.activeTab++;
        }
    }

    confirmTabDelete(tab: any, index: number) {
        this.confirmationService.confirm({
            message: 'Do you want to delete this tab?',
            header: 'Delete Confirmation',
            icon: 'pi pi-info-circle',
            acceptButtonStyleClass: 'btn btn-dialog-danger py-1 px-4',
            rejectButtonStyleClass: 'btn btn-dialog-info py-1 px-4',
            accept: () => {
                this.loadingService.apiLoader = true;
                this.canvasService.deleteTab(tab._id, this.appService.strategyId).subscribe({
                    next: (res) => {
                        this.canvasTabs.splice(index, 1);
                        setTimeout(() => {
                            this.activeTab = 0;
                            this.getCanvas()
                        }
                            , 100);
                    }, error: (e) => {
                        this.loadingService.apiLoader = false;
                        this.cdr.markForCheck();
                        this.sharedService.alertNotification('error', 'Unable to Delete', e);
                    }
                })
            }
        });
    }

    onTabDialogHide() {
        if (this.canvasTabs.length === 0) {
            this.saveTab()
        }
    }

    faTaColor(id: string, colorKey: string) {
        this.faTaColorKey = colorKey;
        const group = dynamicGroups.find((group: any) => group.id === id);
        group.colorKey = colorKey;
        this.renderStencilGroup(this.stencilData[id], group);
        this.canvasData = JSON.stringify(this.rappid.graph.toJSON());
        this.renderGraph();
    }

    filterByCustomProperty(customPropertyValue: string) {
        this.selectedCustomProperty = customPropertyValue;

        // Clear any existing highlights for all cells (elements + links)
        this.rappid.graph.getCells().forEach((cell: any) => {
            this.clearHighlight(cell);
        });

        if (customPropertyValue) {
            // Highlight all cells (elements + links) with matching custom property
            this.rappid.graph.getCells().forEach((cell: any) => {
                const cellData = cell.get('data') || {};
                if (cellData.customProperty === customPropertyValue) {
                    this.applyHighlight(cell);
                }
            });
        }
    }

    private clearHighlight(cell: any): void {
        const isLink = cell.isLink?.() || cell.get('type')?.includes('Link');

        if (isLink) {
            // Restore original link stroke attributes only if cell was highlighted
            if (cell.originalStroke !== undefined || cell.originalStrokeWidth !== undefined) {
                const originalStroke = cell.originalStroke ?? cell.attr(this.LINE_STROKE_PATH) ?? this.DEFAULT_STROKE_COLOR;
                const originalStrokeWidth = cell.originalStrokeWidth ?? cell.attr(this.LINE_STROKE_WIDTH_PATH) ?? this.DEFAULT_STROKE_WIDTH;
                const originalStrokeOpacity = cell.originalStrokeOpacity ?? cell.attr(this.LINE_STROKE_OPACITY_PATH) ?? this.DEFAULT_OPACITY;
                cell.attr(this.LINE_STROKE_PATH, originalStroke);
                cell.attr(this.LINE_STROKE_WIDTH_PATH, originalStrokeWidth);
                cell.attr(this.LINE_STROKE_OPACITY_PATH, originalStrokeOpacity);
            }
            // Clear stored original values
            delete cell.originalStroke;
            delete cell.originalStrokeWidth;
            delete cell.originalStrokeOpacity;
        } else {
            // Restore original element stroke attributes
            const cellType = cell.get('type');

            // Try to restore body attributes for all elements
            if (cell.originalStroke !== undefined || cell.originalStrokeWidth !== undefined) {
                const originalStroke = cell.originalStroke ?? this.DEFAULT_STROKE_COLOR;
                const originalStrokeWidth = cell.originalStrokeWidth ?? this.DEFAULT_STROKE_WIDTH;
                const originalStrokeOpacity = cell.originalStrokeOpacity ?? cell.attr(this.BODY_STROKE_OPACITY_PATH) ?? this.DEFAULT_OPACITY;

                try {
                    cell.attr(this.BODY_STROKE_PATH, originalStroke);
                    cell.attr(this.BODY_STROKE_WIDTH_PATH, originalStrokeWidth);
                    cell.attr(this.BODY_STROKE_OPACITY_PATH, originalStrokeOpacity);
                } catch (e) {
                    // If body attributes don't work, try image stroke for images
                    if (cellType === this.STANDARD_IMAGE_TYPE) {
                        const imageStroke = cell.attr(this.IMAGE_STROKE_PATH);
                        if (imageStroke !== null && imageStroke !== undefined) {
                            cell.attr(this.IMAGE_STROKE_PATH, originalStroke);
                            cell.attr(this.IMAGE_STROKE_WIDTH_PATH, originalStrokeWidth);
                        }
                    }
                }
            }

            // Handle standard.Image - remove wrapper if it exists
            if (cellType === this.STANDARD_IMAGE_TYPE) {
                this.removeImageWrapper(cell);
            }

            // Clear stored original values
            delete cell.originalStroke;
            delete cell.originalStrokeWidth;
            delete cell.originalStrokeOpacity;
        }
    }

    private applyHighlight(cell: any): void {
        const isLink = cell.isLink?.() || cell.get('type')?.includes('Link');

        if (isLink) {
            // Store original link stroke attributes
            const currentLineStroke = cell.attr(this.LINE_STROKE_PATH);
            const currentLineStrokeWidth = cell.attr(this.LINE_STROKE_WIDTH_PATH);
            const currentLineStrokeOpacity = cell.attr(this.LINE_STROKE_OPACITY_PATH);
            cell.originalStroke = (currentLineStroke !== null && currentLineStroke !== undefined) ? currentLineStroke : this.DEFAULT_STROKE_COLOR;
            cell.originalStrokeWidth = (currentLineStrokeWidth !== null && currentLineStrokeWidth !== undefined) ? currentLineStrokeWidth : this.DEFAULT_STROKE_WIDTH;
            cell.originalStrokeOpacity = (currentLineStrokeOpacity !== null && currentLineStrokeOpacity !== undefined) ? currentLineStrokeOpacity : this.DEFAULT_OPACITY;

            // Check if opacity is 0, null, or undefined, make it 1
            if (currentLineStrokeOpacity === 0 || currentLineStrokeOpacity === null || currentLineStrokeOpacity === undefined) {
                cell.attr(this.LINE_STROKE_OPACITY_PATH, this.DEFAULT_OPACITY);
            }

            // Apply black bold border to link (this will override transparent strokes too)
            cell.attr(this.LINE_STROKE_PATH, this.HIGHLIGHT_STROKE_COLOR);
            cell.attr(this.LINE_STROKE_WIDTH_PATH, this.HIGHLIGHT_STROKE_WIDTH);
        } else {
            // For all elements, try to apply body stroke attributes
            const cellType = cell.get('type');

            // Store original element stroke attributes
            const currentBodyStroke = cell.attr(this.BODY_STROKE_PATH);
            const currentBodyStrokeWidth = cell.attr(this.BODY_STROKE_WIDTH_PATH);
            const currentBodyStrokeOpacity = cell.attr(this.BODY_STROKE_OPACITY_PATH);
            cell.originalStroke = (currentBodyStroke !== null && currentBodyStroke !== undefined) ? currentBodyStroke : this.DEFAULT_STROKE_COLOR;
            cell.originalStrokeWidth = (currentBodyStrokeWidth !== null && currentBodyStrokeWidth !== undefined) ? currentBodyStrokeWidth : this.DEFAULT_STROKE_WIDTH;
            cell.originalStrokeOpacity = (currentBodyStrokeOpacity !== null && currentBodyStrokeOpacity !== undefined) ? currentBodyStrokeOpacity : this.DEFAULT_OPACITY;

            // Check if opacity is 0, null, or undefined, make it 1
            if (currentBodyStrokeOpacity === 0 || currentBodyStrokeOpacity === null || currentBodyStrokeOpacity === undefined) {
                cell.attr(this.BODY_STROKE_OPACITY_PATH, this.DEFAULT_OPACITY);
            }

            // Always try to apply body attributes for all elements (rectangles, ellipses, circles, etc.)
            // This works for standard.Rectangle, standard.Ellipse, standard.Circle, etc.
            // This will override transparent strokes and make them visible
            cell.attr(this.BODY_STROKE_PATH, this.HIGHLIGHT_STROKE_COLOR);
            cell.attr(this.BODY_STROKE_WIDTH_PATH, this.HIGHLIGHT_STROKE_WIDTH);

            // For standard.Image, also add wrapper as backup (in case body attributes don't render)
            if (cellType === this.STANDARD_IMAGE_TYPE) {
                // Add wrapper as additional visual indicator
                this.addImageWrapper(cell);
            }
        }
    }

    private addImageWrapper(cell: any): void {
        // Remove existing wrapper if any
        this.removeImageWrapper(cell);

        // Get cell size and position
        const size = cell.get('size') || { width: 100, height: 100 };
        const position = cell.get('position') || { x: 0, y: 0 };

        // Create a wrapper rect element for highlighting using standard Rectangle
        const wrapperId = `highlight-wrapper-${cell.id}`;
        const cellNamespace = this.rappid.graph.get('cellNamespace') as any;
        const RectangleShape = cellNamespace?.standard?.Rectangle || joint.shapes.standard.Rectangle;

        const wrapper = new RectangleShape({
            id: wrapperId,
            position: { x: position.x - 2, y: position.y - 2 },
            size: { width: size.width + 4, height: size.height + 4 },
            attrs: {
                body: {
                    fill: 'transparent',
                    stroke: this.HIGHLIGHT_STROKE_COLOR,
                    strokeWidth: this.HIGHLIGHT_STROKE_WIDTH,
                    pointerEvents: 'none'
                }
            },
            z: cell.get('z') ? cell.get('z') - 1 : -1 // Place behind the image
        });

        // Store wrapper reference and listen to cell position changes
        cell.highlightWrapper = wrapperId;
        this.rappid.graph.addCell(wrapper);

        // Update wrapper position when cell moves
        const updateWrapperPosition = () => {
            const wrapperCell = this.rappid.graph.getCell(wrapperId);
            if (wrapperCell) {
                const newPosition = cell.get('position') || { x: 0, y: 0 };
                const newSize = cell.get('size') || { width: 100, height: 100 };
                wrapperCell.set('position', { x: newPosition.x - 2, y: newPosition.y - 2 });
                wrapperCell.set('size', { width: newSize.width + 4, height: newSize.height + 4 });
            }
        };

        // Store update function and listen to changes
        cell.highlightWrapperUpdate = updateWrapperPosition;
        cell.on('change:position change:size', updateWrapperPosition);
    }

    private removeImageWrapper(cell: any): void {
        if (cell.highlightWrapper) {
            const wrapper = this.rappid.graph.getCell(cell.highlightWrapper);
            if (wrapper) {
                wrapper.remove();
            }
            // Remove event listener
            if (cell.highlightWrapperUpdate) {
                cell.off('change:position change:size', cell.highlightWrapperUpdate);
                cell.highlightWrapperUpdate = undefined;
            }
            cell.highlightWrapper = undefined;
        }
    }

    uploadImage(file: File, cell: dia.Cell) {
        const currentAccountId = this.dashboardService?.accountDetails?.account?._id || this.dashboardService.currentAccId;
        let formData: FormData = new FormData();
        formData.set('pic', file);
        formData.append('acc', currentAccountId);
        formData.append('file_category', this.fileCategory);
        this.dashboardService.UploadImage(formData).subscribe({
            next: (res) => {
                this.dashboardService.getSelectedImage(res.url, this.fileCategory)
                    .subscribe({
                        next: (signedUrl) => {
                            cell.attr('image/xlink:href', signedUrl);
                            cell.attr('meta/url', res.url);
                            this.cdr.markForCheck();
                        },
                        error: () => this.cdr.markForCheck()
                    });
            },
            error: (e) => {
                this.cdr.markForCheck();
                this.sharedService.alertNotification('error', 'Unable to Upload Image', e);
            }
        })
    }

    resetCanvasSize() {
        this.rappid.paper.scale(1);
        this.rappid.paperScroller.positionPoint(new g.Point(0, 0), 0, 0);
    }

    fitCanvas() {
        this.resetCanvasSize();
        const { width, height } = this.rappid.paper.getArea()
        const paperBg = document.querySelector('.paper-scroller-background')
        const paper = document.querySelector('.paper-scroller-background .joint-paper')
        if (paperBg && paper) {
            this.paperStyle = {
                paperBg: paperBg.getAttribute('style'),
                paper: paper.getAttribute('style'),
            }
            document.querySelector('.paper-scroller-background')?.setAttribute('style', `width: ${width}px; height: ${height}px;`);
            document.querySelector('.paper-scroller-background .joint-paper')?.setAttribute('style', `width: ${width}px; height: ${height}px; left: 0; top: 0;`);
            document.querySelector('.joint-paper-scroller')?.scrollTo(0, 0);
        }
    }

    editCanvas() {
        const paperBg = document.querySelector('.paper-scroller-background')
        const paper = document.querySelector('.paper-scroller-background .joint-paper')
        if (paperBg && paper && this.paperStyle.paperBg && this.paperStyle.paper) {
            paperBg.setAttribute('style', this.paperStyle.paperBg);
            paper.setAttribute('style', this.paperStyle.paper);
        }
        this.rappid.paperScroller.positionPoint(new g.Point(0, 0), 0, 0);
    }

    showEditor(cell: any, path: any) {
        this.textEditor = {
            visible: true,
            cell,
            path,
            content: cell.prop(path) || '',
        }
    }

    saveEditorContent() {
        const { cell, content, path } = this.textEditor;
        cell.prop(path, content);
        this.rappid.graph.trigger('change:attrs', cell);
        this.textEditor = {};
    }

    hideEditor() {
        this.textEditor = {}
    }

    showElementOpportunityEditor(cell: any) {
        const cellData = cell.get('data') || {};
        this.elementOpportunityEditor = {
            visible: true,
            cell,
            opportunityType: cellData.customProperty || '',
            content: cellData.elementOpportunityDescription || '',
        };
    }

    saveElementOpportunityDescription() {
        const { cell, content } = this.elementOpportunityEditor;
        if (cell) {
            const cellData = cell.get('data') || {};
            cellData.elementOpportunityDescription = content;
            cell.set('data', cellData);
            this.rappid.graph.trigger('change:data', cell);
            this.handleCanvasChange();
        }
        this.elementOpportunityEditor = {};
    }

    hideOpportunityEditor() {
        this.elementOpportunityEditor = {};
    }

    showElementOpportunityDescriptionPopup(opportunityType: string, description: string) {
        this.elementOpportunityDescriptionPopup = {
            visible: true,
            opportunityType,
            description: description || 'No description available.'
        };
    }

    hideOpportunityDescriptionPopup() {
        this.elementOpportunityDescriptionPopup = {};
    }

    checkPresentationView() {
        if (this.view === 'presentation') {
            const pEl = document.querySelector('#presntation-area')
            if (pEl) {
                this.presentationObserver = observeAttributes(pEl, (attrName: string, value: any) => {
                    if (attrName === 'hidden') {
                        this.renderGraph()
                    }
                })
            }
        }
    }

    ngOnDestroy(): void {
        this.mode = 'view';
        this.rappid?.destroy();
        this.presentationObserver?.disconnect();
        this.destroy$.next();
        this.destroy$.complete();
    }

}
